import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Copy, CheckCircle, ExternalLink, Bot, Zap, Settings } from 'lucide-react';
import { API_URL } from '../config';

const API = API_URL;

const TelegramSetup = () => {
  const [botInfo, setBotInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    fetchBotInfo();
    fetchStats();
  }, []);

  const fetchBotInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/telegram/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBotInfo(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch bot info:', error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/telegram/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#EE1C4E] text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-[#EDEDED] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Bot className="w-10 h-10 text-[#EE1C4E]" />
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Telegram Bot Setup
            </h1>
          </div>
          <p className="text-[#A1A1A1]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Auto-post to RedChannel from your Telegram channel
          </p>
        </div>

        {/* Status Card */}
        <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-3 h-3 rounded-full ${botInfo?.bot_configured ? 'bg-green-500' : 'bg-yellow-500'}`} />
            <h2 className="text-xl font-bold">
              Status: {botInfo?.bot_configured ? 'Configured' : 'Not Configured'}
            </h2>
          </div>
          
          {stats && (
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="bg-[#1A1A1A] p-4 rounded-lg">
                <div className="text-[#A1A1A1] text-sm mb-1">Telegram Posts</div>
                <div className="text-2xl font-bold text-[#EE1C4E]">{stats.telegram_posts}</div>
              </div>
              <div className="bg-[#1A1A1A] p-4 rounded-lg">
                <div className="text-[#A1A1A1] text-sm mb-1">Manual Posts</div>
                <div className="text-2xl font-bold">{stats.manual_posts}</div>
              </div>
              <div className="bg-[#1A1A1A] p-4 rounded-lg">
                <div className="text-[#A1A1A1] text-sm mb-1">Total Posts</div>
                <div className="text-2xl font-bold">{stats.total_posts}</div>
              </div>
            </div>
          )}
        </div>

        {/* Setup Instructions */}
        <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-[#EE1C4E]" />
            <h2 className="text-xl font-bold">Setup Instructions</h2>
          </div>

          <div className="space-y-6">
            {/* Step 1 */}
            <div data-testid="setup-step-1">
              <div className="flex items-start gap-3">
                <div className="bg-[#EE1C4E] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="font-bold mb-2">Create Telegram Bot</h3>
                  <ol className="list-decimal list-inside space-y-1 text-[#A1A1A1] text-sm">
                    <li>Open Telegram and search for <code className="bg-[#1A1A1A] px-2 py-1 rounded">@BotFather</code></li>
                    <li>Send <code className="bg-[#1A1A1A] px-2 py-1 rounded">/newbot</code></li>
                    <li>Choose name: <strong>RedChannel Bot</strong></li>
                    <li>Choose username: <strong>redchannel_post_bot</strong> (or any available name)</li>
                    <li>Save the bot token you receive</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div data-testid="setup-step-2">
              <div className="flex items-start gap-3">
                <div className="bg-[#EE1C4E] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="font-bold mb-2">Add Token to Environment</h3>
                  <p className="text-[#A1A1A1] text-sm mb-2">
                    Add your bot token to <code className="bg-[#1A1A1A] px-2 py-1 rounded">/app/backend/.env</code>
                  </p>
                  <div className="bg-[#1A1A1A] p-3 rounded-lg font-mono text-sm relative">
                    <code>TELEGRAM_BOT_TOKEN=your_bot_token_here</code>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div data-testid="setup-step-3">
              <div className="flex items-start gap-3">
                <div className="bg-[#EE1C4E] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="font-bold mb-2">Set Webhook URL</h3>
                  <p className="text-[#A1A1A1] text-sm mb-2">
                    Run this curl command (replace &lt;YOUR_BOT_TOKEN&gt; with your token):
                  </p>
                  <div className="bg-[#1A1A1A] p-3 rounded-lg font-mono text-xs relative overflow-x-auto">
                    <code className="whitespace-pre-wrap break-all">
{`curl -X POST "https://api.telegram.org/bot<YOUR_BOT_TOKEN>/setWebhook" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "${botInfo?.webhook_url}",
    "secret_token": "${botInfo?.secret_token}",
    "allowed_updates": ["message"],
    "drop_pending_updates": true
  }'`}
                    </code>
                    <button
                      onClick={() => copyToClipboard(`curl -X POST "https://api.telegram.org/bot<YOUR_BOT_TOKEN>/setWebhook" -H "Content-Type: application/json" -d '{"url": "${botInfo?.webhook_url}", "secret_token": "${botInfo?.secret_token}", "allowed_updates": ["message"], "drop_pending_updates": true}'`, 'webhook')}
                      className="absolute top-2 right-2 text-[#A1A1A1] hover:text-white"
                      data-testid="copy-webhook-command"
                    >
                      {copied === 'webhook' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="mt-2 p-2 bg-[#1A1A1A] rounded text-xs">
                    <strong>Webhook URL:</strong> <code>{botInfo?.webhook_url}</code>
                    <button
                      onClick={() => copyToClipboard(botInfo?.webhook_url, 'url')}
                      className="ml-2 text-[#EE1C4E] hover:text-[#FF2E63]"
                    >
                      {copied === 'url' ? '✓' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div data-testid="setup-step-4">
              <div className="flex items-start gap-3">
                <div className="bg-[#EE1C4E] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="font-bold mb-2">Disable Privacy Mode</h3>
                  <ol className="list-decimal list-inside space-y-1 text-[#A1A1A1] text-sm">
                    <li>Send <code className="bg-[#1A1A1A] px-2 py-1 rounded">/setprivacy</code> to @BotFather</li>
                    <li>Select your bot</li>
                    <li>Choose <strong>Disable</strong></li>
                  </ol>
                  <p className="text-xs text-[#A1A1A1] mt-2">
                    ℹ️ This allows the bot to see all messages in groups/channels
                  </p>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div data-testid="setup-step-5">
              <div className="flex items-start gap-3">
                <div className="bg-[#EE1C4E] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  5
                </div>
                <div className="flex-1">
                  <h3 className="font-bold mb-2">Add Bot to Your Channel</h3>
                  <ol className="list-decimal list-inside space-y-1 text-[#A1A1A1] text-sm">
                    <li>Open your Telegram channel</li>
                    <li>Click on channel name → <strong>Administrators</strong></li>
                    <li>Add Administrator → Search for your bot</li>
                    <li>Give permission: <strong>Post Messages</strong></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Message Format Guide */}
        <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-[#EE1C4E]" />
            <h2 className="text-xl font-bold">Message Format</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-bold mb-2 text-[#EE1C4E]">Free Post (with Ad):</h3>
              <div className="bg-[#1A1A1A] p-3 rounded-lg text-sm">
                <pre className="text-[#A1A1A1] whitespace-pre-wrap">
{`Check out this amazing content! 🔥

Download: https://example.com/file/abc123
Unlock: https://example.com/unlock/12345`}
                </pre>
              </div>
            </div>

            <div>
              <h3 className="font-bold mb-2 text-[#EE1C4E]">Premium Post (direct access):</h3>
              <div className="bg-[#1A1A1A] p-3 rounded-lg text-sm">
                <pre className="text-[#A1A1A1] whitespace-pre-wrap">
{`Exclusive premium content 👑

Download: https://example.com/file/xyz789
Premium: https://example.com/premium/key`}
                </pre>
              </div>
            </div>

            <div className="bg-[#1A1A1A] p-4 rounded-lg">
              <h3 className="font-bold mb-2">Link Handling:</h3>
              <ul className="space-y-2 text-sm text-[#A1A1A1]">
                <li className="flex items-start gap-2">
                  <span className="text-[#EE1C4E] font-bold">•</span>
                  <span><strong>Download Link:</strong> Any valid URL → Always accessible to all users</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#EE1C4E] font-bold">•</span>
                  <span><strong>Unlock Link:</strong> Any valid URL → For free users (ad unlock flow)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#EE1C4E] font-bold">•</span>
                  <span><strong>Premium Link:</strong> Any valid URL → Direct access for premium/admin users only</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#EE1C4E] font-bold">•</span>
                  <span><strong>Images:</strong> Automatically extracted and uploaded</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Recent Telegram Posts */}
        {stats?.recent_telegram_posts?.length > 0 && (
          <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6">
            <h2 className="text-xl font-bold mb-4">Recent Telegram Posts</h2>
            <div className="space-y-3">
              {stats.recent_telegram_posts.map((post) => (
                <div key={post.post_id} className="bg-[#1A1A1A] p-3 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm text-[#EDEDED] line-clamp-2">{post.text}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs px-2 py-1 rounded ${
                          post.post_type === 'premium' 
                            ? 'bg-[#EE1C4E] text-white' 
                            : 'bg-[#2A2A2A] text-[#A1A1A1]'
                        }`}>
                          {post.post_type}
                        </span>
                        <span className="text-xs text-[#525252]">
                          {new Date(post.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-8 text-center">
          <a
            href="/admin"
            className="inline-block bg-[#2A2A2A] hover:bg-[#333] text-white px-6 py-2 rounded-full transition-colors"
            data-testid="back-to-admin-btn"
          >
            Back to Admin Dashboard
          </a>
        </div>
      </div>
    </div>
  );
};

export default TelegramSetup;
